import { Checkbox, Input, SelectProps, Space, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';

import { usePostDimensionData } from '@api/emp/queryHooks';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import {
  DimensionSelect,
  DimensionValueDropdownRenderWrapper,
  DimensionValueLabelWrapper,
  DimensionValueSelect,
  StyledIcon,
  StyledTag,
} from '@src/emp-3.0/components/Sankey/components/CompactDualSelect/styled';
import {
  ICompactDualSelect,
  ICompactDualSelectWithRequest,
  ICustomDropdownDualSelectWithRequest,
  IDimensionValueLabelRenderWithCheckbox,
  TCustomLabelRender,
} from '@src/emp-3.0/components/Sankey/components/CompactDualSelect/typed';
import { toLowerCase } from '@src/emp-3.0/utils';
import { isGeoDimension, parseDataToOptions } from '@src/emp-3.0/utils/parse';
import { humanize } from '@utils';

import styles from './index.less';

type TagRender = SelectProps['tagRender'];

const APP_TOTAL_USER_EVENTS = 'app_total_user_events';
const ALL_VALUES = 'All Values';

export const DeleteIcon = (props) => {
  return <StyledIcon component={DeleteSvg} {...props} />;
};

const dimensionValueLabelRender: TCustomLabelRender = (label, item) => (
  <DimensionValueLabelWrapper>
    <Tooltip title={label}>
      <span className="select-item-label-text">{label}</span>
    </Tooltip>
    <span style={{ color: '#949494', flexShrink: 0, lineHeight: '16px', fontSize: '12px' }}>
      {humanize(item[APP_TOTAL_USER_EVENTS], 2)}
    </span>
  </DimensionValueLabelWrapper>
);
const dimensionValueLabelRenderWithCheckbox: React.FC<IDimensionValueLabelRenderWithCheckbox> = ({
  checkboxProps,
  label,
  item,
}) => (
  <div style={{ display: 'flex', gap: '4px' }}>
    <Checkbox {...checkboxProps} />
    {dimensionValueLabelRender(label, item)}
  </div>
);

const dimensionValueDropdownRender = ({ originNode, checked = false, onAllCheckedChange }) => {
  return (
    <DimensionValueDropdownRenderWrapper>
      <Checkbox
        style={{
          color: 'rgba(0, 0, 0, 0.65)',
          letterSpacing: '-0.14px',
          width: '100%',
          borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
          padding: '6px 8px',
        }}
        checked={checked}
        onChange={onAllCheckedChange}
      >
        All Values
      </Checkbox>
      <div>
        <div
          style={{
            padding: '6px 12px',
            color: 'rgba(0, 0, 0, 0.25)',
            fontWeight: 500,
            letterSpacing: '-0.12px',
          }}
        >
          Top 500
        </div>
        {originNode}
      </div>
    </DimensionValueDropdownRenderWrapper>
  );
};

const dimensionValueTagRender: TagRender = (props) => {
  const { label, value, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  if (value === ALL_VALUES) {
    return <span style={{ paddingLeft: '10px' }}>{label}</span>;
  }

  return (
    <StyledTag onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose}>
      {label}
    </StyledTag>
  );
};

const CompactDualSelect: React.FC<ICompactDualSelect> = ({
  dimensionSelectProps = {},
  dimensionValueSelectProps = {},
  prefix,
  showDelete = true,
  deleteRender,
  onDelete,
  style,
}) => {
  const {
    value: dimensionName,
    options: nameOptions = [],
    onChange: onNameChange,
    ...restDimensionSelectProps
  } = dimensionSelectProps;
  const {
    placeholder: valuePlaceHolder,
    value: dimensionValueOrValues,
    options: valueOptions = [],
    onChange: onValueChange,
    style: dimensionValueSelectStyle,
    maxCount,
    ...resetSelectDimensionValueSelectProps
  } = dimensionValueSelectProps;
  const dimensionValue = maxCount === 1 ? dimensionValueOrValues?.[0] : dimensionValueOrValues;

  const DeleteButton = () => {
    if (!showDelete) return null;
    return deleteRender ? (
      (deleteRender(<DeleteIcon />) as JSX.Element)
    ) : (
      <DeleteIcon onClick={onDelete} />
    );
  };

  return (
    <Space.Compact style={style}>
      {prefix}
      <DimensionSelect
        placeholder="Dimension..."
        value={dimensionName || undefined}
        options={nameOptions}
        filterOption={(inputValue, option = []) =>
          typeof option?.label === 'string'
            ? toLowerCase(option?.label ?? '').includes(toLowerCase(inputValue))
            : true
        }
        dropdownStyle={{ minWidth: '300px' }}
        showLeftBorder={!prefix}
        showSearch
        onChange={(value, option) => {
          onNameChange?.(value, option);
        }}
        {...restDimensionSelectProps}
      />
      <Input
        style={{
          width: 30,
          pointerEvents:
            dimensionSelectProps.disabled && dimensionValueSelectProps.disabled ? 'auto' : 'none',
          borderRight: 0,
          borderLeft: 0,
          marginInlineEnd: 0,
          flexShrink: 0,
          color: 'rgba(0, 0, 0, 0.88)',
        }}
        value="="
        disabled
      />
      <DimensionValueSelect
        mode={maxCount === 1 ? undefined : 'multiple'}
        maxTagCount={1}
        placeholder={valuePlaceHolder || 'Value...'}
        style={{ minWidth: 83, ...dimensionValueSelectStyle }}
        value={dimensionValue || undefined}
        options={valueOptions.map((x) => {
          let disabled = x.disabled;
          if (maxCount === 1) {
            return {
              label: x.label,
              value: x.value,
              key: x.key,
              code: x.code || '',
              disabled,
            };
          }
          if (
            Array.isArray(dimensionValue) &&
            dimensionValue.length >= (maxCount || Number.MAX_SAFE_INTEGER) &&
            !dimensionValue.includes(x.value)
          ) {
            disabled = true;
          }

          return {
            label: x.label,
            value: x.value,
            key: x.key,
            code: x.code || '',
            disabled,
          };
        })}
        filterOption={(inputValue, option) =>
          typeof option?.label === 'string'
            ? toLowerCase(option?.label ?? '').includes(toLowerCase(inputValue))
            : toLowerCase(option?.value ?? '').includes(toLowerCase(inputValue))
        }
        showRightBorder={!showDelete}
        dropdownStyle={{ minWidth: '450px' }}
        showSearch
        allowClear={maxCount === 1 ? false : true}
        onChange={(value, option) => {
          const valueFomat = maxCount === 1 ? [value] : value;
          onValueChange?.(valueFomat, option);
        }}
        {...resetSelectDimensionValueSelectProps}
      />
      <DeleteButton />
    </Space.Compact>
  );
};

const CompactDualSelectWithRequest: React.FC<ICompactDualSelectWithRequest> = ({
  dimensionSelectProps,
  dimensionValueSelectProps = {},
  disabledValueOptions,
  interval,
  filters,
  ...rest
}) => {
  const { labelRender, dropdownRender, ...restDimensionValueSelectProps } =
    dimensionValueSelectProps;
  const { mutate: getDimensionData, isLoading: isGettingDimensionData } = usePostDimensionData();
  const [dimensionValueOptions, setDimensionValueOptions] = useState<any[]>([]);

  useEffect(() => {
    if (!interval) {
      return;
    }
    const dimensionField = dimensionSelectProps?.value;
    dimensionField &&
      getDimensionData(
        { params: { src: 'trends' }, fields: [dimensionField], interval, filters },
        {
          onSuccess: (data) => {
            const options = parseDataToOptions(data?.data?.[0], {
              label: dimensionField,
              value: isGeoDimension(dimensionField) ? `${dimensionField}_code` : dimensionField,
              code: `${dimensionField}_code`,
              disabledCallback: (item) =>
                (disabledValueOptions || []).includes(item[dimensionField]),
              labelRender,
            });
            setDimensionValueOptions(options ?? []);
          },
          onError: (err) => {
            setDimensionValueOptions([]);
          },
        },
      );
  }, [
    interval,
    dimensionSelectProps?.value,
    getDimensionData,
    filters,
    disabledValueOptions,
    labelRender,
  ]);
  return (
    <CompactDualSelect
      dimensionSelectProps={dimensionSelectProps}
      dimensionValueSelectProps={{
        ...restDimensionValueSelectProps,
        loading: isGettingDimensionData,
        options: dimensionValueOptions,
        popupClassName: styles.compactDualSelectPopup,
        dropdownRender: (originNode) =>
          dimensionValueOptions.length > 0 && dropdownRender
            ? dropdownRender(originNode)
            : originNode,
      }}
      {...rest}
    />
  );
};
// CustomDropdownDualSelectWithRequest based on CompactDualSelectWithRequest encapsulation, follow-up 2 component integration can be considered a component.
const CustomDropdownDualSelectWithRequest: React.FC<ICustomDropdownDualSelectWithRequest> = ({
  allChecked: allCheckedProps = false,
  onAllCheckedChange,
  disabledValueOptions,
  dimensionSelectProps = {},
  dimensionValueSelectProps = {},
  ...rest
}) => {
  const { value: dimensionField } = dimensionSelectProps;
  const {
    value: dimensionValue,
    onChange: onDimensionValueChange,
    mode: dimensionValueMode,
  } = dimensionValueSelectProps;

  const [allChecked, setAllChecked] = useState<boolean>(allCheckedProps);

  useEffect(() => {
    setAllChecked(allCheckedProps);
  }, [allCheckedProps]);

  const handleAllCheckedChange = (e) => {
    const checked = e.target.checked;
    setAllChecked(checked);
    onAllCheckedChange?.(checked);
  };

  const handleDimensionValueChange = (value, option) => {
    onDimensionValueChange?.(value, option);
  };

  if (!dimensionValueMode) {
    return (
      <CompactDualSelectWithRequest
        dimensionSelectProps={dimensionSelectProps}
        dimensionValueSelectProps={{
          labelRender: dimensionValueLabelRender,
          filterOption: (inputValue, option) =>
            toLowerCase(option?.value ?? '').includes(toLowerCase(inputValue)),
          ...dimensionValueSelectProps,
        }}
        {...rest}
      />
    );
  }
  return (
    <CompactDualSelectWithRequest
      disabledValueOptions={disabledValueOptions}
      dimensionSelectProps={dimensionSelectProps}
      dimensionValueSelectProps={{
        ...dimensionValueSelectProps,
        mode: dimensionValueMode,
        value: allChecked ? [ALL_VALUES] : dimensionValue,
        tagRender: dimensionValueTagRender,
        dropdownRender: (originNode) =>
          dimensionValueDropdownRender({
            originNode,
            checked: allChecked,
            onAllCheckedChange: handleAllCheckedChange,
          }),
        labelRender: (label, item) => {
          const checked = allChecked
            ? false
            : (dimensionValue ?? []).includes(item[dimensionField]);
          const isDisabledWhenAllChecked =
            dimensionValueMode === 'multiple' &&
            disabledValueOptions?.includes('') &&
            [''].includes(item[dimensionField]);
          const disabled = allChecked
            ? isDisabledWhenAllChecked
            : (disabledValueOptions || []).includes(item[dimensionField]);

          return dimensionValueLabelRenderWithCheckbox({
            checkboxProps: { checked, disabled },
            label,
            item,
          });
        },
        onChange: handleDimensionValueChange,
        filterOption: (inputValue, option) =>
          toLowerCase(option?.value ?? '').includes(toLowerCase(inputValue)),
        className: styles.compactDualSelectWithRequest,
      }}
      {...rest}
    />
  );
};

export {
  CompactDualSelect,
  CompactDualSelectWithRequest,
  CustomDropdownDualSelectWithRequest,
  dimensionValueLabelRender,
  dimensionValueLabelRenderWithCheckbox,
  dimensionValueDropdownRender,
};
